import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/apiService";
import { useError } from "../context/ErrorContext";

const BillingSettings = () => {
  const navigate = useNavigate();
  const { showError } = useError();
  const [loading, setLoading] = useState(true);
  const [billingInfo, setBillingInfo] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    loadBillingInfo();
  }, []);

  const loadBillingInfo = async () => {
    try {
      const data = await apiService.get("/api/billing");
      setBillingInfo(data.billing);
      setPaymentMethods(data.paymentMethods);
    } catch (error) {
      showError("Failed to load billing information");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async (plan) => {
    try {
      const session = await apiService.post("/api/billing/create-payment", {
        plan,
      });
      window.location.href = session.url;
    } catch (error) {
      showError("Failed to initiate payment");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg text-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Billing Settings</h1>

        {/* Current Plan */}
        <div className="bg-dark-card rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Current Plan</h2>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-2xl font-bold text-blue-500">
                {billingInfo?.plan || "Free"}
              </p>
              <p className="text-gray-400">
                {billingInfo?.status === "active" && billingInfo?.nextBilling
                  ? `Next billing date: ${new Date(
                      billingInfo.nextBilling
                    ).toLocaleDateString()}`
                  : "No active subscription"}
              </p>
            </div>
            <button
              onClick={() => navigate("/settings/subscription")}
              className="px-4 py-2 text-blue-500 hover:bg-blue-500/10 rounded-lg transition-colors"
            >
              Change Plan
            </button>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-dark-card rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Payment Methods</h2>
          {paymentMethods.length > 0 ? (
            <div className="space-y-4">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className="flex justify-between items-center p-4 bg-dark-bg rounded-lg"
                >
                  <div className="flex items-center">
                    <div className="mr-4">
                      {method.brand === "visa" ? "Visa" : "Card"} ••••{" "}
                      {method.last4}
                    </div>
                    <div className="text-gray-400">
                      Expires {method.expMonth}/{method.expYear}
                    </div>
                  </div>
                  {method.isDefault && (
                    <span className="text-sm text-green-500">Default</span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400">No payment methods added</p>
          )}
          <button
            onClick={() => handleUpdatePaymentMethod(billingInfo?.plan)}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            {paymentMethods.length > 0
              ? "Update Payment Method"
              : "Add Payment Method"}
          </button>
        </div>

        {/* Billing History */}
        <div className="bg-dark-card rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Billing History</h2>
          {billingInfo?.invoices?.length > 0 ? (
            <div className="space-y-4">
              {billingInfo.invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex justify-between items-center p-4 bg-dark-bg rounded-lg"
                >
                  <div>
                    <div className="font-medium">
                      {new Date(invoice.date).toLocaleDateString()}
                    </div>
                    <div className="text-gray-400">{invoice.description}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-medium">${invoice.amount}</span>
                    <a
                      href={invoice.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-400"
                    >
                      Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400">No billing history available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingSettings;
